import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Send,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Mail,
  Calendar,
  User,
  Building,
  IndianRupee,
  AlertTriangle,
  Download,
  Edit,
  Trash2,
  X,
  ArrowLeft,
  Plus,
  FileText,
  Paperclip,
  MapPin,
  CreditCard,
  Truck,
  Package,
  ShoppingCart,
  Route
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import {
  handleTextInputChange,
  handleSearchInputChange,
  getCharacterCount,
  validateRequiredFields,
  FIELD_LIMITS
} from '../../../utils/inputValidation';
import PermissionGate from '../../../components/common/PermissionGate';
import { formatDate } from '../../../utils/stringUtils';
import { formatIndianAmount } from '../../../utils/amountFormatter';
import { getQuotationStatusClasses } from '../../../utils/quotationStatusUtils';
import quotationTrackingService from '../../../api/crm/quotations/quotationTrackingService';
import quotationService from '../../../api/crm/quotations/quotationService';
import orderService from '../../../api/crm/orders/orderService';
import margDataService from '../../../api/crm/marg/margDataService';
import SendQuoteModal from './components/SendQuoteModal';

const SendTrack = () => {
  const navigate = useNavigate();
  const { quoteId } = useParams();
  const [searchParams] = useSearchParams();
  const pageType = searchParams.get('page'); // 'quote', 'order', or 'ordered'
  const queryClient = useQueryClient();

  // Dynamic titles based on page source
  const pageTitle = pageType === 'ordered' ? 'Order' : (pageType === 'order' ? 'Quotation' : 'Details');
  const idLabel = pageType === 'ordered' ? 'Order ID' : (pageType === 'order' ? 'Quotation ID' : 'Quote ID');

  // Default to Customer tab when page opens
  const [activeTab, setActiveTab] = useState('customer');
  const [activeTermsTab, setActiveTermsTab] = useState('terms'); // Sub-tab for Terms & Conditions section
  const [showSendModal, setShowSendModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [showOrderPlacedConfirm, setShowOrderPlacedConfirm] = useState(false);
  const [margOrderId, setMargOrderId] = useState('');

  // Fetch tracking data with React Query
  const { data: trackingData, isLoading: isTrackingLoading, isError: isTrackingError, refetch: refetchTrackingData } = useQuery({
    queryKey: ['quotationTracking', quoteId],
    queryFn: () => quotationTrackingService.getTrackingData(quoteId),
    enabled: !!quoteId
  });

  // Extract data from trackingData
  const quote = trackingData?.data?.quote || null;
  const communicationLog = trackingData?.data?.communicationLog || [];
  const reminders = trackingData?.data?.reminders || [];
  const statusTimeline = trackingData?.data?.statusTimeline || [];

  // Fetch quotation details with React Query
  const { data: quotationDetailsData, isLoading: isQuotationDetailsLoading, isError: isQuotationDetailsError, refetch: refetchQuotationDetails } = useQuery({
    queryKey: ['quotationDetails', quoteId, pageType],
    queryFn: () => {
      const params = {};
      if (pageType === 'ordered') {
        params.ordered = 'true';
      }
      return quotationService.getQuotationById(quoteId, params);
    },
    enabled: !!quoteId
  });

  // Redirect to dashboard if no quoteId provided (as per PRD - Q2 requires quoteId)
  React.useEffect(() => {
    if (quoteId === undefined || quoteId === null) {
      navigate('/crm/quotation-proposal/dashboard');
    }
  }, [quoteId, navigate]);

  // Extract quotation details
  const quotationDetails = quotationDetailsData?.quotation || null;
  const quotationItems = quotationDetails?.items || [];
  // Note: customerId and contactId removed from API response - use Lead or Customer data instead
  const quotationLead = quotationDetails?.lead || null;
  const quotationCustomer = quotationDetails?.customer || null;

  // Fetch shipment data for ordered items
  // Only fetch when:
  // 1. pageType is 'ordered' (meaning API was called with ordered=true)
  // 2. quotationDetails have been loaded successfully
  // 3. quotationDetails has an orderId
  // 4. User has clicked on the "Track Shipment" tab (activeTab === 'trackShipment')
  const { data: shipmentDataResponse, isLoading: isShipmentLoading } = useQuery({
    queryKey: ['orderShipment', quotationDetails?.orderId],
    queryFn: async () => {
      if (!quotationDetails?.orderId || pageType !== 'ordered') return null;

      try {
        // Use the new dedicated API endpoint to fetch specific shipment details
        const response = await margDataService.getOrderShipmentByOrderId(quotationDetails.orderId);

        if (response.success && response.shipment) {
          return response.shipment;
        }
        return null;
      } catch (error) {

        return null;
      }
    },
    // Only enable when:
    // - quotation details are loaded
    // - pageType is 'ordered'
    // - orderId exists
    // - User has clicked on Track Shipment tab
    enabled: !!quotationDetails && pageType === 'ordered' && !!quotationDetails.orderId && activeTab === 'trackShipment'
  });

  // Update shipment data and tab visibility
  React.useEffect(() => {
    if (shipmentDataResponse && pageType === 'ordered') {
      setShipmentData(shipmentDataResponse);
      setShowTrackShipmentTab(true);
    } else if (pageType === 'ordered' && quotationDetails?.orderId) {
      // Show tab if orderId exists, even if shipment data hasn't been fetched yet
      setShowTrackShipmentTab(true);
    } else {
      setShipmentData(null);
      setShowTrackShipmentTab(false);
    }
  }, [shipmentDataResponse, pageType, quotationDetails?.orderId]);

  // Normalized customer display info (derived from quotation + lead/customer)
  const customerDisplay = {
    companyName:
      quotationDetails?.billingAddress?.company ||
      quotationDetails?.shippingAddress?.company ||
      quotationCustomer?.companyName ||
      quotationLead?.company ||
      '',
    email:
      quotationCustomer?.email ||
      quotationLead?.email ||
      quotationDetails?.billingAddress?.gstin || // Fallback - no email in billing
      '',
    phone:
      quotationDetails?.shippingAddress?.phone ||
      quotationCustomer?.phone ||
      quotationLead?.phone ||
      '',
    contactPerson:
      quotationCustomer?.contactPerson ||
      (quotationLead?.firstName && quotationLead?.lastName
        ? `${quotationLead.firstName} ${quotationLead.lastName}`.trim()
        : quotationLead?.firstName || quotationLead?.lastName || '')
  };

  // Modal states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);

  // Shipment tracking states
  const [shipmentData, setShipmentData] = useState(null);
  const [showTrackShipmentTab, setShowTrackShipmentTab] = useState(false);

  // Track if we are navigating to confirmation (to preserve edits)
  const isConfirmingRef = React.useRef(false);

  // Email editing state
  const [isEmailEditable, setIsEmailEditable] = useState(false);

  // Form states for modals
  const [sendForm, setSendForm] = useState({
    email: '',
    message: ''
  });

  const [reminderForm, setReminderForm] = useState({
    type: 'followup',
    scheduledFor: '',
    message: ''
  });
  const [errors, setErrors] = useState({});

  // Helper function to get email from either source (opportunity-based or customer-based)
  const getEmailForSend = () => {
    // 0. Use quotation specific email if available
    if (quotationDetails?.email && quotationDetails.email.trim() && quotationDetails.email.includes('@')) {
      return quotationDetails.email.trim();
    }
    // First, try quote.contactEmail (works for opportunity-based quotations)
    if (quote && quote.contactEmail && quote.contactEmail !== 'N/A' && quote.contactEmail.trim() && quote.contactEmail.includes('@')) {
      return quote.contactEmail.trim();
    }
    // If not available, try customerDisplay.email (works for customer-based quotations)
    if (customerDisplay.email && customerDisplay.email !== 'N/A' && customerDisplay.email.trim() && customerDisplay.email.includes('@')) {
      return customerDisplay.email.trim();
    }
    return '';
  };

  // Mutations
  const sendQuotationMutation = useMutation({
    mutationFn: ({ isResend, quoteId, email, message }) => {
      if (isResend) {
        return quotationTrackingService.resendQuote(quoteId, email, message);
      } else {
        return quotationService.sendQuotation(quoteId, {
          to: email,
          message: message || ''
        });
      }
    },
    onSuccess: (data) => {
      // CRITICAL: Only update UI and invalidate queries if email was successfully sent
      // Backend ensures status is only changed if email succeeds
      if (data.success) {
        // Invalidate and refetch tracking data (status will be updated on backend)
        queryClient.invalidateQueries({ queryKey: ['quotationTracking', quoteId] });
        queryClient.invalidateQueries({ queryKey: ['quotationDetails', quoteId] });
        setShowSendModal(false);
        // Reset form handling is now inside the modal component
        alert('Quotation sent successfully!');
      } else {
        // Email failed - do NOT invalidate queries (status should remain unchanged)
        // Backend already ensures status is not changed on email failure
        const errorMsg = data.error || 'Failed to send email';
        alert(`Email sending failed: ${errorMsg}`);
      }
    },
    onError: (error) => {
      // CRITICAL: Email sending failed - do NOT invalidate queries
      // This ensures status remains unchanged (backend already prevents status change)

      const errorMessage = error?.response?.data?.responseMessage ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to send email';

      // Show clear error message explaining that status remains unchanged
      alert(`Email sending failed: ${errorMessage}`);
    }
  });

  const deleteQuotationMutation = useMutation({
    mutationFn: (quoteId) => quotationService.deleteQuotation(quoteId),
    onSuccess: (data) => {
      if (data.success) {
        alert('Quotation deleted successfully');
        navigate('/crm/quotation-proposal/create-quotes');
      } else {
        alert(data.error || 'Failed to delete quotation');
      }
    },
    onError: (error) => {

      alert('Failed to delete quotation');
    },
    onSettled: () => {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  });

  const reminderMutation = useMutation({
    mutationFn: ({ isEditing, quoteId, reminderId, reminderData }) => {
      if (isEditing) {
        return quotationTrackingService.updateReminder(quoteId, reminderId, reminderData);
      } else {
        return quotationTrackingService.addReminder(quoteId, reminderData);
      }
    },
    onSuccess: (data) => {
      if (data.success) {
        // Invalidate and refetch tracking data
        queryClient.invalidateQueries({ queryKey: ['quotationTracking', quoteId] });
        setReminderForm({ type: 'followup', scheduledFor: '', message: '' });
        setErrors({});
        setEditingReminder(null);
        setShowReminderModal(false);
      } else {
        alert(data.error || 'Failed to save reminder');
      }
    },
    onError: (error) => {

      alert('Failed to save reminder');
    }
  });

  const deleteReminderMutation = useMutation({
    mutationFn: ({ quoteId, reminderId }) => quotationTrackingService.deleteReminder(quoteId, reminderId),
    onSuccess: (data) => {
      if (data.success) {
        // Invalidate and refetch tracking data
        queryClient.invalidateQueries({ queryKey: ['quotationTracking', quoteId] });
      } else {
        alert(data.error || 'Failed to delete reminder');
      }
    },
    onError: (error) => {

      alert('Failed to delete reminder');
    }
  });

  const exportLogMutation = useMutation({
    mutationFn: (quoteId) => quotationTrackingService.exportLog(quoteId),
    onSuccess: (data) => {
      if (!data.success) {
        alert(data.error || 'Failed to export log');
      }
    },
    onError: (error) => {

      alert('Failed to export log');
    }
  });

  // Helper function to generate order number from quote ID
  // Converts Q-2025-0010 to ORD20250010
  const generateOrderNumberFromQuoteId = (quoteId) => {
    if (!quoteId) return '';
    // Remove Q- prefix and all dashes, then add ORD prefix
    const cleaned = quoteId.toString().replace(/^Q-?/i, '').replace(/-/g, '');
    return `ORD${cleaned}`;
  };

  // Navigate to Quote Builder for editing
  const handleEditQuote = () => {
    const idToUse = quote._id || quote.quoteId || quote.id;
    if (!idToUse) {

      return;
    }
    navigate(`/crm/quotation-proposal/quote-builder?quotationId=${idToUse}`);
  };

  // Mutation: create order from quotation (when status is "order")
  const createOrderFromQuotationMutation = useMutation({
    mutationFn: async (margOrderIdInput) => {
      if (!quoteId) {
        throw new Error('Invalid quotation ID');
      }

      // Fetch full quotation details to build order payload
      const quoteResult = await quotationService.getQuotationById(quoteId);
      if (!quoteResult.success || !quoteResult.quotation) {
        throw new Error(quoteResult.error || 'Failed to load quotation details');
      }

      const quotation = quoteResult.quotation;

      // Generate order number from quote ID
      const quoteIdForOrder = quotation.quoteNumber || quotation.quoteId || quoteId;
      const generatedOrderNumber = generateOrderNumberFromQuoteId(quoteIdForOrder);

      // Use Lead data instead of Customer/Contact collections
      // Note: customerId is no longer required - data will be fetched from quotation -> lead -> opportunity
      const lead = quotation.lead || {};
      const billingAddressSrc = quotation.billingAddress || {};
      const shippingAddressSrc = quotation.shippingAddress || {};

      // Create customer-like object from Lead data (for order creation compatibility)
      const customer = {
        companyName: lead.company || billingAddressSrc.company || '',
        name: lead.company || billingAddressSrc.company || '',
        email: lead.email || '',
        phone: lead.phone || shippingAddressSrc.phone || '',
        contactPerson: lead.firstName && lead.lastName
          ? `${lead.firstName} ${lead.lastName}`.trim()
          : lead.firstName || lead.lastName || '',
        gstin: billingAddressSrc.gstin || '',
        address: lead.address?.street || '',
        city: lead.address?.city || '',
        state: lead.address?.state || '',
        pincode: lead.address?.pincode || '',
        billingAddress: {
          address: lead.address?.street || '',
          street: lead.address?.street || '',
          city: lead.address?.city || '',
          state: lead.address?.state || '',
          pincode: lead.address?.pincode || ''
        },
        shippingAddress: {
          address: lead.address?.street || '',
          street: lead.address?.street || '',
          city: lead.address?.city || '',
          state: lead.address?.state || '',
          pincode: lead.address?.pincode || ''
        }
      };

      // Create contact-like object from Lead data
      const contact = {
        email: lead.email || '',
        phone: lead.phone || '',
        firstName: lead.firstName || '',
        lastName: lead.lastName || '',
        designation: lead.designation || ''
      };

      // Helper function to validate and convert date
      const parseDate = (dateValue) => {
        if (!dateValue) return null;
        // If it's already a Date object, check if valid
        if (dateValue instanceof Date) {
          return isNaN(dateValue.getTime()) ? null : dateValue;
        }
        // If it's a string, try to parse it
        if (typeof dateValue === 'string') {
          // If it's an empty string or just whitespace, return null
          if (!dateValue.trim()) return null;
          const parsed = new Date(dateValue);
          return isNaN(parsed.getTime()) ? null : parsed.toISOString();
        }
        // Try to convert to Date
        try {
          const parsed = new Date(dateValue);
          return isNaN(parsed.getTime()) ? null : parsed.toISOString();
        } catch {
          return null;
        }
      };

      // Map items from quotation to order items
      const items = Array.isArray(quotation.items)
        ? quotation.items.map((item) => ({
          productId: item.productId || null,
          productName: item.productName,
          sku: item.sku || '',
          description: item.description || '',
          quantity: item.quantity,
          mrp: item.mrp || 0,
          unitPrice: item.unitPrice,
          unitPricePTS: item.unitPricePTS || 0,
          batch: item.batch || '',
          expiry: parseDate(item.expiry),
          manufacturing: parseDate(item.manufacturing),
          discount: item.discount || 0,
          tax: item.tax || 0,
          lineTotal: item.lineTotal || 0
        }))
        : [];

      if (!items.length) {
        throw new Error('Quotation has no items. Cannot create order.');
      }

      const orderData = {
        quotationId: quotation._id,
        quoteId: quotation.quoteNumber || quotation.quoteId || null, // Quote number (e.g., "Q-2025-0010")
        orderId: margOrderIdInput, // Use the user-inputted MARG ID as the orderId
        orderNumber: margOrderIdInput, // Use MARG ID as orderNumber as well
        email: contact.email || customer.email || '',
        phone: contact.phone || customer.phone || '',
        billingAddress: {
          company:
            billingAddressSrc.company ||
            customer.companyName ||
            customer.name ||
            'N/A',
          address:
            billingAddressSrc.address ||
            billingAddressSrc.street ||
            customer.billingAddress?.address ||
            customer.billingAddress?.street ||
            customer.address ||
            '',
          city:
            billingAddressSrc.city ||
            customer.billingAddress?.city ||
            customer.city ||
            '',
          state:
            billingAddressSrc.state ||
            customer.billingAddress?.state ||
            customer.state ||
            '',
          pincode:
            billingAddressSrc.pincode ||
            customer.billingAddress?.pincode ||
            customer.pincode ||
            '',
          gstin: billingAddressSrc.gstin || customer.gstin || ''
        },
        shippingAddress: {
          company:
            shippingAddressSrc.company ||
            billingAddressSrc.company ||
            customer.companyName ||
            customer.name ||
            'N/A',
          address:
            shippingAddressSrc.address ||
            shippingAddressSrc.street ||
            billingAddressSrc.address ||
            billingAddressSrc.street ||
            customer.shippingAddress?.address ||
            customer.shippingAddress?.street ||
            customer.billingAddress?.address ||
            customer.billingAddress?.street ||
            customer.address ||
            '',
          landmark: shippingAddressSrc.landmark || '',
          city:
            shippingAddressSrc.city ||
            billingAddressSrc.city ||
            customer.shippingAddress?.city ||
            customer.billingAddress?.city ||
            customer.city ||
            '',
          state:
            shippingAddressSrc.state ||
            billingAddressSrc.state ||
            customer.shippingAddress?.state ||
            customer.billingAddress?.state ||
            customer.state ||
            '',
          pincode:
            shippingAddressSrc.pincode ||
            billingAddressSrc.pincode ||
            customer.shippingAddress?.pincode ||
            customer.billingAddress?.pincode ||
            customer.pincode ||
            '',
          contact: shippingAddressSrc.contact || '',
          phone: shippingAddressSrc.phone || contact.phone || customer.phone || '',
          deliveryInstructions: shippingAddressSrc.deliveryInstructions || quotation.notes || ''
        },
        items,
        subtotal: quotation.subtotal || 0,
        totalDiscount: quotation.discountAmount || 0,
        totalTax: quotation.taxAmount || 0,
        grandTotal: quotation.totalAmount || 0,
        status: 'confirmed',
        deliveryType: 'Standard (5-7 days)',
        notes: quotation.notes || ''
      };

      const createResult = await orderService.createOrder(orderData);
      if (!createResult.success) {
        throw new Error(createResult.error || 'Failed to create order');
      }

      return createResult;
    },
    onSuccess: () => {
      // Refresh tracking data and quotation details
      queryClient.invalidateQueries({ queryKey: ['quotationTracking', quoteId] });
      queryClient.invalidateQueries({ queryKey: ['quotationDetails', quoteId] });
      setShowOrderPlacedConfirm(false);
      setMargOrderId(''); // Reset input
      alert('Order placed successfully');
    },
    onError: (error) => {

      alert(error.message || 'Failed to create order from quotation');
    }
  });

  // No need for useEffect as React Query handles data fetching



  // Check if quotation has been updated since last send
  const hasQuotationUpdates = () => {
    if (!quote) {
      return false;
    }

    // If never sent, allow sending
    if (!quote.sentDate) {
      return true;
    }

    // Check if updatedAt is significantly after sentDate (more than 60 seconds / 1 minute)
    // This accounts for the time it takes to mark as sent and avoids false positives
    if (quote.updatedAt && quote.sentDate) {
      const updatedAt = new Date(quote.updatedAt);
      const sentDate = new Date(quote.sentDate);

      // Calculate time difference in milliseconds
      const timeDiff = updatedAt.getTime() - sentDate.getTime();

      // Only allow resend if quotation was updated more than 60 seconds (1 minute) after sending
      // This ensures it was an actual content update, not just the send process updating the timestamp
      // When sending, sentDate and updatedAt are usually set within seconds of each other
      if (timeDiff > 60000) { // 60 seconds (1 minute) buffer
        return true;
      }
    }

    // Status changes (seen, accepted, rejected) don't count as content updates
    // These are just tracking states, not actual quotation edits

    // Default: no updates detected, disable resend
    return false;
  };

  const getChannelIcon = (channel) => {
    switch (channel?.toLowerCase()) {
      case 'email': return <Mail className="h-4 w-4" />;
      default: return <Mail className="h-4 w-4" />;
    }
  };

  const getChannelColor = (channel) => {
    switch (channel?.toLowerCase()) {
      case 'email': return 'text-[#0F2942]';
      default: return 'text-gray-600';
    }
  };

  // Format date without time (for Owner and Expiry Date)
  const formatDateOnly = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format date as dd-mm-yyyy
  const formatDateDDMMYYYY = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if invalid date
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      return dateString; // Return original if parsing fails
    }
  };

  const formatLocation = (val) => {
    if (!val) return null;
    if (typeof val === 'object') return val.name || '';
    return val;
  };

  // Load quotation details when View tab is active
  // React Query handles this automatically with the useQuery hook




  const handleSend = () => {
    setShowSendModal(true);
  };

  // Actually send/resend the quote via email (called from confirmation modal)
  const handleExecuteSend = async ({ email, message, isResend }) => {
    sendQuotationMutation.mutate({
      isResend,
      quoteId,
      email,
      message
    });
  };

  // Delete quotation
  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    deleteQuotationMutation.mutate(quoteId);
  };

  const handleScheduleReminder = () => {
    setShowReminderModal(true);
  };

  // Schedule the reminder
  const handleConfirmScheduleReminder = async () => {
    const requiredFields = ['scheduledFor', 'message'];
    const validation = validateRequiredFields(reminderForm, requiredFields);

    if (!validation.isValid) {
      setErrors(validation.errors);
      alert('Please fill in all required fields');
      return;
    }

    const reminderData = {
      type: reminderForm.type,
      scheduledFor: reminderForm.scheduledFor,
      message: reminderForm.message
    };

    reminderMutation.mutate({
      isEditing: !!editingReminder,
      quoteId,
      reminderId: editingReminder?._id,
      reminderData
    });
  };

  // Delete reminder
  const handleDeleteReminder = async (reminderId) => {
    if (window.confirm('Are you sure you want to delete this reminder?')) {
      deleteReminderMutation.mutate({ quoteId, reminderId });
    }
  };

  // Edit reminder
  const handleEditReminder = (reminder) => {
    setEditingReminder(reminder);
    setReminderForm({
      type: reminder.type,
      scheduledFor: new Date(reminder.scheduledFor).toISOString().slice(0, 16),
      message: reminder.message
    });
    setShowReminderModal(true);
  };

  const handleExportLog = async () => {
    exportLogMutation.mutate(quoteId);
  };

  // Generate and download invoice PDF
  const handleDownloadInvoice = async () => {
    if (!quotationDetails || downloadingInvoice) return;

    try {
      setDownloadingInvoice(true);

      const result = await quotationService.downloadInvoice(quoteId);

      if (!result.success) {
        alert(result.error || 'Failed to download invoice PDF. Please try again.');
      }

      setDownloadingInvoice(false);
    } catch (error) {

      alert('Failed to download invoice PDF. Please try again.');
      setDownloadingInvoice(false);
    }
  };

  const getAttachmentDownloadFilename = (attachment) => {
    if (!attachment) return "document.pdf";
    const fallbackName = attachment.filename || attachment.originalName || "document";
    if (/\.[a-zA-Z0-9]+$/.test(fallbackName)) return fallbackName;

    // Default to .pdf if no extension is found
    const mimeType = (attachment.mimetype || attachment.mimeType || "").toLowerCase();
    if (mimeType === "application/pdf") return `${fallbackName}.pdf`;
    return `${fallbackName}.pdf`;
  };

  const handleAttachmentDownload = async (attachment) => {
    if (!attachment || !attachment.url) return;

    const url = attachment.url;
    const filename = getAttachmentDownloadFilename(attachment);

    try {
      const downloadToast = toast.loading('Preparing download...');

      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch attachment");

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
      toast.dismiss(downloadToast);
    } catch (error) {

      // Fallback: Just open in new tab if blob download fails
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Show loading state
  if (!quoteId || isTrackingLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="bg-white border border-gray-200 rounded-lg p-12 w-full max-w-2xl">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-200 rounded w-48 mx-auto"></div>
            <div className="h-4 bg-gray-200 rounded w-32 mx-auto"></div>
            <div className="space-y-3 mt-8">
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              <div className="h-4 bg-gray-200 rounded w-4/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error if quote not found
  if (isTrackingError || !quote) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Failed to load quotation data. Please try again.</p>
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="mt-4"
          >
            Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              icon={<ArrowLeft className="h-4 w-4" />}
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {pageTitle}
              </h1>
              <p className="text-gray-600">
                {idLabel}: {pageType === 'ordered'
                  ? (quotationDetails?.orderId || 'N/A')
                  : (quotationDetails?.quoteNumber || quote.quoteNumber || quote.quoteId || 'N/A')}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {/* Download Invoice button - show if status is order/ordered, or if on order page and accepted */}
            {quotationDetails && (
              (['ordered', 'order'].includes(pageType) && ['accepted', 'order', 'ordered'].includes(quote.status?.toLowerCase())) ||
              (['order', 'ordered'].includes(quote.status?.toLowerCase()))
            ) && (
                <PermissionGate permission="quotation_management" action="read">
                  <Button
                    variant="outline"
                    icon={<Download className="h-4 w-4" />}
                    onClick={handleDownloadInvoice}
                    disabled={downloadingInvoice}
                    className="text-[#0F2942] hover:text-[#0B2033] border-blue-300 hover:bg-blue-50"
                  >
                    {downloadingInvoice ? 'Generating...' : 'Download Invoice'}
                  </Button>
                </PermissionGate>
              )}
            {/* Show "Edit" button for all statuses except accepted, order, ordered, converted */}
            {!['accepted', 'order', 'ordered', 'converted'].includes(quote.status?.toLowerCase()) && (
              <PermissionGate permission="quotation_management" action="update">
                <Button
                  variant="outline"
                  icon={<Edit className="h-4 w-4" />}
                  onClick={handleEditQuote}
                  className="text-green-600 hover:text-green-700 border-green-300 hover:bg-green-50"
                >
                  Edit
                </Button>
              </PermissionGate>
            )}
            {/* Show "Order Placed" button if status is "order" */}
            {quote.status?.toLowerCase() === 'order' && (
              <PermissionGate permission="order_management" action="create">
                <Button
                  variant="primary"
                  icon={<Package className="h-4 w-4" />}
                  onClick={() => {
                    setMargOrderId('');
                    setShowOrderPlacedConfirm(true);
                  }}
                  className="bg-green-600 hover:bg-green-700"
                  disabled={createOrderFromQuotationMutation.isPending}
                >
                  {createOrderFromQuotationMutation.isPending ? 'Creating...' : 'Order Placed'}
                </Button>
              </PermissionGate>
            )}
            {/* Show "Send" button for draft status, "Resend" for sent/seen/rejected */}
            {/* Hide both buttons for accepted, order, and ordered statuses */}
            {quote.status?.toLowerCase() === 'draft' && (
              <PermissionGate permission="quotation_management" action="update">
                <Button
                  variant="primary"
                  icon={<Send className="h-4 w-4" />}
                  onClick={handleSend}
                  className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                >
                  Send
                </Button>
              </PermissionGate>
            )}
            {['sent', 'seen', 'rejected'].includes(quote.status?.toLowerCase()) && (
              <PermissionGate permission="quotation_management" action="update">
                <Button
                  variant="primary"
                  icon={<Send className="h-4 w-4" />}
                  onClick={handleSend}
                  className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                  title="Resend quotation"
                >
                  Resend
                </Button>
              </PermissionGate>
            )}
            {/* Hide delete button for accepted, order, ordered statuses when on quote or ordered page */}
            {!(
              ['accepted', 'order', 'ordered'].includes(quote.status?.toLowerCase()) &&
              (pageType === 'quote' || pageType === 'ordered' || pageType === 'order')
            ) && (
                <PermissionGate permission="quotation_management" action="delete">
                  <Button
                    variant="outline"
                    icon={<Trash2 className="h-4 w-4" />}
                    onClick={handleDelete}
                    className="text-red-600 hover:text-red-700 border-red-300 hover:bg-red-50"
                  >
                    Delete
                  </Button>
                </PermissionGate>
              )}
          </div>
        </div>
      </div>

      {/* Quote Summary */}
      <div>
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="flex items-start space-x-3 min-w-0">
              <User className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-600 mb-1">Customer Name</p>
                <p className="font-medium text-gray-900 break-words capitalize" title={customerDisplay.contactPerson || quote.contactName || 'N/A'}>
                  {customerDisplay.contactPerson || quote.contactName || 'N/A'}
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3 min-w-0">
              <IndianRupee className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-600 mb-1">{['order', 'ordered'].includes(quote.status?.toLowerCase()) ? 'Order Value' : 'Quote Value'}</p>
                <p className="font-medium text-gray-900 whitespace-nowrap">₹{quote.totalAmount?.toLocaleString('en-IN') || '0'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3 min-w-0">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-600 mb-1">Status</p>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getQuotationStatusClasses(
                    (['accepted', 'order', 'ordered'].includes(quote.status?.toLowerCase()) && pageType === 'quote') ? 'accepted' : quote.status
                  )}`}>
                    {(['accepted', 'order', 'ordered'].includes(quote.status?.toLowerCase()) && pageType === 'quote') ? 'accepted' : quote.status}
                  </span>
                  {(['accepted', 'order', 'ordered'].includes(quote.status?.toLowerCase()) && pageType === 'quote') && (
                    <span className="text-xs text-gray-500 font-medium">
                      {quote.status?.toLowerCase() === 'order'
                        ? '(Ready to order)'
                        : quote.status?.toLowerCase() === 'ordered'
                          ? '(Ordered)'
                          : ''}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {quotationDetails?.deliveryType && (
              <div className="flex items-start space-x-3 min-w-0">
                <Truck className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 mb-1">Delivery Type</p>
                  <p className="font-medium text-gray-900 break-words">{quotationDetails.deliveryType}</p>
                </div>
              </div>
            )}
            {quotationDetails?.plannedDispatchDate && (
              <div className="flex items-start space-x-3 min-w-0">
                <Calendar className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 mb-1">Dispatch Date</p>
                  <p className="font-medium text-gray-900 break-words">
                    {formatDate(quotationDetails.plannedDispatchDate, { includeTime: false })}
                  </p>
                </div>
              </div>
            )}
            {quotationDetails?.expiryDate && (
              <div className="flex items-start space-x-3 min-w-0">
                <Calendar className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 mb-1">Expiry Date</p>
                  <p className="font-medium text-gray-900 break-words">
                    {formatDate(new Date(quotationDetails.expiryDate), { includeTime: false })}
                  </p>
                </div>
              </div>
            )}
            {quotationDetails?.email && (
              <div className="flex items-start space-x-3 min-w-0">
                <Mail className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 mb-1">Quote Email</p>
                  <p className="font-medium text-gray-900 break-words">
                    {quotationDetails.email}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
        {quotationDetails?.rejectReason && quote.status?.toLowerCase() === 'rejected' && (
          <div className="p-3 mt-5 bg-red-50 rounded-lg border border-red-200">
            <div className="flex items-start space-x-2">
              <XCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-red-800">Rejection Reason</span>
                <p className="text-sm text-red-700 mt-1 break-words first-letter:uppercase">{quotationDetails?.rejectReason}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6 overflow-x-auto">
            {[
              { key: 'customer', label: 'Customer', icon: User },
              { key: 'billing', label: 'Billing Address', icon: CreditCard },
              { key: 'shipping', label: 'Shipping Address', icon: Truck },
              { key: 'items', label: 'Items', icon: ShoppingCart },
              { key: 'attachments', label: 'Attachments', icon: Paperclip },
              { key: 'terms', label: 'Terms & Conditions', icon: FileText },
              { key: 'status', label: 'Status', icon: CheckCircle },
              ...(showTrackShipmentTab && pageType === 'ordered' ? [{ key: 'trackShipment', label: 'Tracking', icon: Truck }] : [])
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 whitespace-nowrap ${activeTab === tab.key
                  ? 'border-agrosaf-green text-agrosaf-green'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Loading State */}
          {isQuotationDetailsLoading && (
            <div className="animate-pulse space-y-4 py-12">
              <div className="h-6 bg-gray-200 rounded w-48 mx-auto"></div>
              <div className="h-4 bg-gray-200 rounded w-32 mx-auto"></div>
              <div className="space-y-3 mt-8">
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                <div className="h-4 bg-gray-200 rounded w-4/6"></div>
              </div>
            </div>
          )}

          {/* Error State */}
          {isQuotationDetailsError && !isQuotationDetailsLoading && (
            <div className="text-center py-12">
              <p className="text-gray-600">Failed to load quotation details</p>
            </div>
          )}

          {/* Customer Tab */}
          {activeTab === 'customer' && quotationDetails && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Company / Phone from normalized customerDisplay (quotation + lead) */}
                <div className="flex items-start space-x-3 min-w-0">
                  <Building className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-600">Company</p>
                    <p className="font-medium text-gray-900 break-words capitalize">
                      {customerDisplay.companyName || 'N/A'}
                    </p>
                  </div>
                </div>
                {/* Contact Person from customer or lead when available */}
                {customerDisplay.contactPerson && (
                  <div className="flex items-start space-x-3 min-w-0">
                    <User className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600">Customer</p>
                      <p className="font-medium text-gray-900 break-words capitalize">
                        {customerDisplay.contactPerson}
                      </p>
                      {quotationLead?.designation && (
                        <p className="text-xs text-gray-500 capitalize mt-1">{quotationLead.designation}</p>
                      )}
                    </div>
                  </div>
                )}
                {customerDisplay.phone && (
                  <div className="flex items-start space-x-3 min-w-0">
                    <User className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600">Phone</p>
                      <p className="font-medium text-gray-900 break-words">
                        {customerDisplay.phone}
                      </p>
                    </div>
                  </div>
                )}
                {/* Show lead email */}
                {customerDisplay.email && (
                  <div className="flex items-start space-x-3 min-w-0">
                    <Mail className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-medium text-gray-900 break-words">
                        {customerDisplay.email}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Billing Address Tab */}
          {activeTab === 'billing' && quotationDetails && (
            <div className="space-y-6">
              {quotationDetails?.billingAddress || quotationLead?.address ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start space-x-3 min-w-0">
                    <Building className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600">Company</p>
                      <p className="font-medium text-gray-900 break-words capitalize">
                        {quotationDetails.billingAddress?.company ||
                          quotationLead?.company ||
                          'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 min-w-0">
                    <MapPin className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600">House no. / Street</p>
                      <p className="font-medium text-gray-900 break-words capitalize">
                        {quotationDetails.billingAddress?.address ||
                          quotationDetails.billingAddress?.street ||
                          quotationLead?.address?.street ||
                          'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 min-w-0">
                    <MapPin className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600">City</p>
                      <p className="font-medium text-gray-900 break-words capitalize">
                        {formatLocation(quotationDetails.billingAddress?.city) ||
                          formatLocation(quotationLead?.address?.city) ||
                          'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 min-w-0">
                    <MapPin className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600">State</p>
                      <p className="font-medium text-gray-900 break-words capitalize">
                        {formatLocation(quotationDetails.billingAddress?.state) ||
                          formatLocation(quotationLead?.address?.state) ||
                          'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 min-w-0">
                    <MapPin className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600">Pincode</p>
                      <p className="font-medium text-gray-900 break-words">
                        {quotationDetails.billingAddress?.pincode ||
                          quotationLead?.address?.pincode ||
                          'N/A'}
                      </p>
                    </div>
                  </div>
                  {quotationDetails.billingAddress?.gstin && (
                    <div className="flex items-start space-x-3 min-w-0">
                      <CreditCard className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-600">GSTIN</p>
                        <p className="font-medium text-gray-900 break-words uppercase">
                          {quotationDetails.billingAddress.gstin}
                        </p>
                      </div>
                    </div>
                  )}
                  {quotationDetails.billingAddress?.drugLicenseNumber && (
                    <div className="flex items-start space-x-3 min-w-0">
                      <CreditCard className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-600">Drug License Number</p>
                        <p className="font-medium text-gray-900 break-words uppercase">
                          {quotationDetails.billingAddress.drugLicenseNumber}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">No billing address available</p>
              )}
            </div>
          )}

          {/* Shipping Address Tab */}
          {activeTab === 'shipping' && quotationDetails && (
            <div className="space-y-6">
              {quotationDetails?.shippingAddress || quotationDetails?.billingAddress || quotationLead?.address ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start space-x-3 min-w-0">
                    <Building className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600">Company</p>
                      <p className="font-medium text-gray-900 break-words capitalize">
                        {quotationDetails.shippingAddress?.company ||
                          quotationDetails.billingAddress?.company ||
                          quotationLead?.company ||
                          'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 min-w-0">
                    <MapPin className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600">House no. / Street</p>
                      <p className="font-medium text-gray-900 break-words capitalize">
                        {quotationDetails.shippingAddress?.address ||
                          quotationDetails.shippingAddress?.street ||
                          quotationDetails.billingAddress?.address ||
                          quotationDetails.billingAddress?.street ||
                          quotationLead?.address?.street ||
                          'N/A'}
                      </p>
                      {quotationDetails.shippingAddress?.landmark && (
                        <p className="mt-1 text-xs text-gray-600 capitalize break-words">
                          Landmark: {quotationDetails.shippingAddress.landmark}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 min-w-0">
                    <MapPin className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600">City</p>
                      <p className="font-medium text-gray-900 break-words capitalize">
                        {formatLocation(quotationDetails.shippingAddress?.city) ||
                          formatLocation(quotationDetails.billingAddress?.city) ||
                          formatLocation(quotationLead?.address?.city) ||
                          'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 min-w-0">
                    <MapPin className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600">State</p>
                      <p className="font-medium text-gray-900 break-words capitalize">
                        {formatLocation(quotationDetails.shippingAddress?.state) ||
                          formatLocation(quotationDetails.billingAddress?.state) ||
                          formatLocation(quotationLead?.address?.state) ||
                          'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 min-w-0">
                    <MapPin className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600">Pincode</p>
                      <p className="font-medium text-gray-900 break-words">
                        {quotationDetails.shippingAddress?.pincode ||
                          quotationDetails.billingAddress?.pincode ||
                          quotationLead?.address?.pincode ||
                          'N/A'}
                      </p>
                    </div>
                  </div>
                  {quotationDetails.shippingAddress?.contact && (
                    <div className="flex items-start space-x-3 min-w-0">
                      <User className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-600">Contact Person</p>
                        <p className="font-medium text-gray-900 break-words capitalize">
                          {quotationDetails.shippingAddress.contact}
                        </p>
                      </div>
                    </div>
                  )}
                  {quotationDetails.shippingAddress?.phone && (
                    <div className="flex items-start space-x-3 min-w-0">
                      <User className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-600">Phone</p>
                        <p className="font-medium text-gray-900 break-words">
                          {quotationDetails.shippingAddress.phone}
                        </p>
                      </div>
                    </div>
                  )}
                  {quotationDetails.shippingAddress?.deliveryInstructions && (
                    <div className="flex items-start space-x-3 min-w-0 md:col-span-2">
                      <MapPin className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-600">Delivery Instructions</p>
                        <p className="font-medium text-gray-900 break-words capitalize">
                          {quotationDetails.shippingAddress.deliveryInstructions}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">No shipping address available</p>
              )}
            </div>
          )}

          {/* Items Tab */}
          {activeTab === 'items' && quotationDetails && (
            <div className="space-y-6">
              {quotationItems.length > 0 ? (
                <>
                  <div className="overflow-x-auto -mx-6 px-6">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product Name</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">MRP</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Manufacturer</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Discount %</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tax %</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Line Total</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {quotationItems.map((item, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 break-words text-sm font-medium text-gray-900 capitalize">
                              {item.productName || 'N/A'}
                            </td>
                            <td className="px-4 py-3 break-words text-sm text-gray-600">{item.quantity || 0}</td>
                            <td className="px-4 py-3 break-words text-sm text-gray-600">₹{(item.mrp || 0).toLocaleString('en-IN')}</td>
                            <td className="px-4 py-3 break-words text-sm text-gray-600">
                              ₹{item.unitPrice?.toLocaleString('en-IN') || '0'}
                              {item.priceOverride && <span className="text-red-600 text-xs ml-1">(Override)</span>}
                            </td>
                            <td className="px-4 py-3 break-words text-sm text-gray-600">{formatDateDDMMYYYY(item.expiry)}</td>
                            <td className="px-4 py-3 break-words text-sm text-gray-600">{item.manufacturing || '-'}</td>
                            <td className="px-4 py-3 break-words text-sm text-gray-600">{item.discount || 0}%</td>
                            <td className="px-4 py-3 break-words text-sm text-gray-600">{item.tax || 0}%</td>
                            <td className="px-4 py-3 break-words text-sm font-medium text-gray-900">₹{item.lineTotal?.toLocaleString('en-IN') || '0'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Totals */}
                  <div className="mt-6 flex justify-end">
                    <div className="w-80 space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Subtotal:</span>
                        <span className="font-medium">₹{quotationDetails.subtotal?.toLocaleString('en-IN') || '0'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Discount:</span>
                        <span className="font-medium text-green-600">-₹{quotationDetails.discountAmount?.toLocaleString('en-IN') || '0'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Taxable Amount:</span>
                        <span className="font-medium">₹{((quotationDetails.subtotal || 0) - (quotationDetails.discountAmount || 0)).toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tax:</span>
                        <span className="font-medium">₹{quotationDetails.taxAmount?.toLocaleString('en-IN') || '0'}</span>
                      </div>
                      <div className="flex justify-between border-t border-gray-200 pt-2">
                        <span className="text-lg font-semibold text-gray-900">Total:</span>
                        <span className="text-lg font-bold text-agrosaf-green">₹{quotationDetails.totalAmount?.toLocaleString('en-IN') || '0'}</span>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-gray-500">No items available</p>
              )}
            </div>
          )}

          {/* Attachments Tab */}
          {activeTab === 'attachments' && quotationDetails && (
            <div className="space-y-6">
              {quotationDetails.attachments && quotationDetails.attachments.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {quotationDetails.attachments.map((attachment, index) => (
                    <div
                      key={index}
                      className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex-shrink-0">
                        <FileText className="h-8 w-8 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate" title={attachment.filename || attachment.originalName || `Attachment ${index + 1}`}>
                          {attachment.filename || attachment.originalName || `Attachment ${index + 1}`}
                        </p>
                        {attachment.size && (
                          <p className="text-xs text-gray-500">
                            {(attachment.size / 1024).toFixed(2)} KB
                          </p>
                        )}
                      </div>
                      {attachment.url && (
                        <button
                          type="button"
                          onClick={() => handleAttachmentDownload(attachment)}
                          className="flex-shrink-0 text-agrosaf-green hover:text-agrosaf-darkGreen"
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No attachments available</p>
              )}
            </div>
          )}

          {/* Terms & Conditions Tab */}
          {activeTab === 'terms' && quotationDetails && (
            <div className="space-y-6">
              {/* Sub-tabs for Terms & Notes */}
              <div className="border-b border-gray-200">
                <nav className="flex space-x-8">
                  <button
                    type="button"
                    onClick={() => setActiveTermsTab('terms')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTermsTab === 'terms'
                      ? 'border-agrosaf-green text-agrosaf-green'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                  >
                    <FileText className="h-4 w-4" />
                    <span>Terms & Conditions</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTermsTab('notes')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTermsTab === 'notes'
                      ? 'border-agrosaf-green text-agrosaf-green'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                  >
                    <FileText className="h-4 w-4" />
                    <span>Additional Notes</span>
                  </button>
                </nav>
              </div>

              {/* Terms & Conditions Content */}
              {activeTermsTab === 'terms' && (
                <div>
                  {quotationDetails.terms ? (() => {
                    let filteredTerms = quotationDetails.terms
                      .replace(/Converted to Order:\s*/gi, '')
                      .replace(/ORD\d+\s+on\s+\d{1,2}\/\d{1,2}\/\d{4},\s+\d{1,2}:\d{2}:\d{2}\s+(AM|PM)/gi, '')
                      .trim();
                    return (
                      <div>
                        <p className="text-sm text-gray-600 mb-2">Terms & Conditions</p>
                        <div className="prose max-w-none">
                          <p className="text-gray-700 whitespace-pre-wrap break-words" style={{ textTransform: 'capitalize' }}>
                            {filteredTerms}
                          </p>
                        </div>
                      </div>
                    );
                  })() : (
                    <p className="text-gray-500">No terms & conditions available</p>
                  )}
                </div>
              )}

              {/* Additional Notes Content */}
              {activeTermsTab === 'notes' && (
                <div>
                  {quotationDetails.notes ? (() => {
                    let filteredNotes = quotationDetails.notes
                      .replace(/Converted to Order:\s*/gi, '')
                      .replace(/ORD\d+\s+on\s+\d{1,2}\/\d{1,2}\/\d{4},\s+\d{1,2}:\d{2}:\d{2}\s+(AM|PM)/gi, '')
                      .trim();
                    return (
                      <div>
                        <p className="text-sm text-gray-600 mb-2">Additional Notes</p>
                        <div className="prose max-w-none">
                          <p className="text-gray-700 whitespace-pre-wrap break-words" style={{ textTransform: 'capitalize' }}>
                            {filteredNotes}
                          </p>
                        </div>
                      </div>
                    );
                  })() : (
                    <p className="text-gray-500">No additional notes available</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Status & Tracking Tab */}
          {activeTab === 'status' && (
            <div>
              <div className="grid grid-cols-2 gap-20">
                <div className="grid grid-cols-1 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Quote Status</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-600">Current Status</span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getQuotationStatusClasses(quote.status)}`}>
                          {quote.status}
                        </span>
                      </div>
                      {quote.sentDate && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm text-gray-600">Sent Date</span>
                          <span className="text-sm font-medium text-gray-900">{formatDate(quote.sentDate)}</span>
                        </div>
                      )}
                      {quote.seenDate && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm text-gray-600">Seen Date</span>
                          <span className="text-sm font-medium text-gray-900">{formatDate(quote.seenDate)}</span>
                        </div>
                      )}
                      {quote.acceptedDate && (
                        <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                          <span className="text-sm text-gray-600">Accepted Date</span>
                          <span className="text-sm font-medium text-green-700">{formatDate(quote.acceptedDate)}</span>
                        </div>
                      )}
                      {quote.rejectedDate && (
                        <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                          <span className="text-sm text-gray-600">Rejected Date</span>
                          <span className="text-sm font-medium text-red-700">{formatDate(quote.rejectedDate)}</span>
                        </div>
                      )}
                      {quote.status?.toLowerCase() === 'order' && quote.updatedAt && (
                        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                          <span className="text-sm text-gray-600">Order Date</span>
                          <span className="text-sm font-medium text-[#0B2033]">{formatDate(quote.updatedAt)}</span>
                        </div>
                      )}
                      {quote.status?.toLowerCase() === 'ordered' && quote.updatedAt && (
                        <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
                          <span className="text-sm text-gray-600">Ordered Date</span>
                          <span className="text-sm font-medium text-indigo-700">{formatDate(quote.updatedAt)}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-600">Expiry Date</span>
                        <span className="text-sm font-medium text-gray-900">{formatDateOnly(quote.expiryDate)}</span>
                      </div>
                    </div>
                  </div>

                </div>

                {/* Status Timeline */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Status Timeline</h3>
                  <div className="space-y-4">
                    {statusTimeline.length === 0 ? (
                      <p className="text-sm text-gray-500">No timeline data available</p>
                    ) : (
                      // Filter out "Finalized" status from timeline
                      statusTimeline
                        .filter(item => item.label.toLowerCase() !== 'finalized')
                        .map((item, index) => {
                          const getIcon = (label) => {
                            if (label.toLowerCase().includes('created')) return <CheckCircle className="h-5 w-5 text-green-600" />;
                            if (label.toLowerCase().includes('sent')) return <Send className="h-5 w-5 text-yellow-600" />;
                            if (label.toLowerCase().includes('viewed') || label.toLowerCase().includes('seen')) return <Eye className="h-5 w-5 text-purple-600" />;
                            if (label.toLowerCase().includes('accepted')) return <CheckCircle className="h-5 w-5 text-green-600" />;
                            if (label.toLowerCase().includes('rejected')) return <XCircle className="h-5 w-5 text-red-600" />;
                            if (label.toLowerCase().includes('order') && !label.toLowerCase().includes('ordered')) return <ShoppingCart className="h-5 w-5 text-[#0F2942]" />;
                            if (label.toLowerCase().includes('ordered')) return <Package className="h-5 w-5 text-indigo-600" />;
                            return <CheckCircle className="h-5 w-5 text-gray-600" />;
                          };

                          const getBgColor = (label) => {
                            if (label.toLowerCase().includes('created')) return 'bg-green-100';
                            if (label.toLowerCase().includes('sent')) return 'bg-yellow-100';
                            if (label.toLowerCase().includes('viewed') || label.toLowerCase().includes('seen')) return 'bg-purple-100';
                            if (label.toLowerCase().includes('accepted')) return 'bg-green-100';
                            if (label.toLowerCase().includes('rejected')) return 'bg-red-100';
                            if (label.toLowerCase().includes('order') && !label.toLowerCase().includes('ordered')) return 'bg-blue-100';
                            if (label.toLowerCase().includes('ordered')) return 'bg-indigo-100';
                            return 'bg-gray-100';
                          };

                          return (
                            <div key={index} className="flex items-center space-x-4">
                              <div className="flex-shrink-0">
                                <div className={`w-8 h-8 ${getBgColor(item.label)} rounded-full flex items-center justify-center`}>
                                  {getIcon(item.label)}
                                </div>
                              </div>
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900 capitalize">{item.label}</p>
                                <p className="text-sm text-gray-600">{formatDate(item.timestamp)}</p>
                              </div>
                            </div>
                          );
                        })
                    )}
                  </div>
                </div>
              </div>
              {/* Show rejection reason here if status is NOT 'rejected' but reason exists */}
              {quotationDetails?.rejectReason && quote.status?.toLowerCase() !== 'rejected' && (
                <div className="p-3 mt-5 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-start space-x-2">
                    <XCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-red-800">Previously Rejected Reason</span>
                      <p className="text-sm text-red-700 mt-1 break-words first-letter:uppercase">{quotationDetails?.rejectReason}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Track Shipment Tab */}
          {activeTab === 'trackShipment' && pageType === 'ordered' && (
            <div className="space-y-6">
              {/* Loading State */}
              {isShipmentLoading && (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-pulse space-y-4 w-full">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                  </div>
                </div>
              )}

              {/* No Shipment Data */}
              {!isShipmentLoading && !shipmentData && (
                <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <Package className="h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Shipment Information</h3>
                  <p className="text-sm text-gray-500 text-center max-w-md">
                    Shipment tracking information is not available for this order yet.
                    Please check back later or contact support for more details.
                  </p>
                </div>
              )}

              {/* Shipment Data Available */}
              {!isShipmentLoading && shipmentData && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Shipment Details */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900">Shipment Details</h3>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm text-gray-600">Order ID</span>
                          <span className="text-sm font-medium text-gray-900">{shipmentData.orderId || 'N/A'}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm text-gray-600">AWB Number</span>
                          <span className="text-sm font-medium text-gray-900">{shipmentData.awbNumber || 'N/A'}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm text-gray-600">Courier Partner</span>
                          <span className="text-sm font-medium text-gray-900 capitalize">{shipmentData.courier || shipmentData.courierPartner || 'N/A'}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm text-gray-600">Status</span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${shipmentData.shipmentStatus === 'DEL' ? 'bg-green-100 text-green-800' :
                            shipmentData.shipmentStatus === 'INT' ? 'bg-blue-100 text-blue-800' :
                              shipmentData.shipmentStatus === 'OOD' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-800'
                            }`}>
                            {shipmentData.shipmentStatusLabel || shipmentData.shipmentStatus || 'N/A'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm text-gray-600">Last Updated</span>
                          <span className="text-sm font-medium text-gray-900">
                            {shipmentData.lastShipmentSync ? formatDate(shipmentData.lastShipmentSync) : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Additional Shipment Info */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900">Additional Information</h3>
                      <div className="space-y-3">
                        {shipmentData.from && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm text-gray-600">From</span>
                            <span className="text-sm font-medium text-gray-900 uppercase">{shipmentData.from}</span>
                          </div>
                        )}
                        {shipmentData.to && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm text-gray-600">To</span>
                            <span className="text-sm font-medium text-gray-900 uppercase">{shipmentData.to}</span>
                          </div>
                        )}
                        {shipmentData.customerName && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm text-gray-600">Customer Name</span>
                            <span className="text-sm font-medium text-gray-900 capitalize">{shipmentData.customerName}</span>
                          </div>
                        )}
                        {shipmentData.pickupDate && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm text-gray-600">Pickup Date</span>
                            <span className="text-sm font-medium text-gray-900">{formatDate(shipmentData.pickupDate)}</span>
                          </div>
                        )}
                        {shipmentData.trackingUrl && (
                          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                            <span className="text-sm text-gray-600">Tracking URL</span>
                            <a
                              href={shipmentData.trackingUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium text-[#0F2942] hover:text-blue-800 underline"
                            >
                              Track Package
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Shipment Tracking Timeline */}
                  {shipmentData?.scanHistory && shipmentData.scanHistory.length > 0 && (
                    <div className="mt-8">
                      <h3 className="text-lg font-semibold text-gray-900 mb-6">Shipment Tracking Timeline</h3>
                      <div className="space-y-4">
                        {shipmentData.scanHistory
                          .sort((a, b) => new Date(b.time) - new Date(a.time)) // Most recent first
                          .map((scan, index) => {
                            const isDelivered = scan.status_detail?.toLowerCase().includes('delivered');
                            const isOutForDelivery = scan.status_detail?.toLowerCase().includes('out for delivery');
                            const isInTransit = scan.status_detail?.toLowerCase().includes('transit');

                            const getStatusColor = () => {
                              if (isDelivered) return 'bg-green-500';
                              if (isOutForDelivery) return 'bg-yellow-500';
                              if (isInTransit) return 'bg-blue-500';
                              return 'bg-gray-500';
                            };

                            const getStatusIcon = () => {
                              if (isDelivered) return <CheckCircle className="h-4 w-4 text-white" />;
                              if (isOutForDelivery) return <Truck className="h-4 w-4 text-white" />;
                              if (isInTransit) return <Package className="h-4 w-4 text-white" />;
                              return <Clock className="h-4 w-4 text-white" />;
                            };

                            return (
                              <div key={index} className="flex items-start space-x-4">
                                {/* Timeline dot */}
                                <div className="flex-shrink-0">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getStatusColor()}`}>
                                    {getStatusIcon()}
                                  </div>
                                  {/* Timeline line */}
                                  {index < shipmentData.scanHistory.length - 1 && (
                                    <div className="w-0.5 h-8 bg-gray-300 mx-auto mt-2"></div>
                                  )}
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0 pb-8">
                                  <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                                    <div className="flex items-center justify-between mb-2">
                                      <h4 className="text-sm font-medium text-gray-900">
                                        {scan.status_detail || 'Status Update'}
                                      </h4>
                                      <span className="text-xs text-gray-500">
                                        {formatDate(scan.time, { includeTime: false })}
                                      </span>
                                    </div>
                                    {scan.location && scan.location.trim() && (
                                      <div className="flex items-center text-sm text-gray-600 mb-2">
                                        <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                                        <span className="uppercase">{scan.location}</span>
                                      </div>
                                    )}
                                    <div className="text-xs text-gray-500">
                                      {new Date(scan.time).toLocaleTimeString('en-IN', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: true
                                      })}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Confirm Delete</h3>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="text-gray-400 hover:text-gray-600"
                disabled={deleting}
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-6">
              <div className="flex items-start space-x-3 mb-4">
                <AlertTriangle className="h-6 w-6 text-red-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900 mb-2">Are you sure you want to delete this quotation?</p>
                  <p className="text-sm text-gray-600">This action cannot be undone. All associated data will be permanently deleted.</p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
                icon={deleteQuotationMutation.isPending ? <div className="h-4 w-4 bg-gray-200 rounded animate-pulse" /> : <Trash2 className="h-4 w-4" />}
              >
                {deleteQuotationMutation.isPending ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Send Confirmation Modal */}
      <SendQuoteModal
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
        quote={quote}
        defaultEmail={getEmailForSend()}
        onSend={handleExecuteSend}
        isSending={sendQuotationMutation.isPending}
      />

      {/* Schedule Reminder Modal */}
      {showReminderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Schedule Reminder</h3>
              <button
                onClick={() => {
                  setShowReminderModal(false);
                  setErrors({});
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reminder Type
                </label>
                <select
                  value={reminderForm.type}
                  onChange={(e) => setReminderForm({ ...reminderForm, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                >
                  <option value="followup" className="capitalize">Follow-up</option>
                  <option value="expiry" className="capitalize">Expiry Reminder</option>
                  <option value="custom" className="capitalize">Custom</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Schedule Date & Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  value={reminderForm.scheduledFor}
                  onChange={(e) => {
                    setReminderForm({ ...reminderForm, scheduledFor: e.target.value });
                    if (errors.scheduledFor) {
                      setErrors(prev => ({ ...prev, scheduledFor: '' }));
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.scheduledFor ? 'border-red-300' : 'border-gray-300'
                    }`}
                  required
                />
                {errors.scheduledFor && (
                  <p className="text-xs text-red-600 mt-1">{errors.scheduledFor}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <textarea
                    rows={3}
                    value={reminderForm.message}
                    onChange={(e) => {
                      const sanitized = handleTextInputChange(e, null, FIELD_LIMITS.message);
                      setReminderForm({ ...reminderForm, message: sanitized });
                      if (errors.message) {
                        setErrors(prev => ({ ...prev, message: '' }));
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.message ? 'border-red-300' : 'border-gray-300'
                      }`}
                    placeholder="Enter reminder message..."
                    maxLength={FIELD_LIMITS.message}
                    required
                  />
                  <div className="absolute bottom-2 right-2 text-xs text-gray-500">
                    {getCharacterCount(reminderForm.message, FIELD_LIMITS.message)}
                  </div>
                </div>
                {errors.message && (
                  <p className="text-xs text-red-600 mt-1">{errors.message}</p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowReminderModal(false)}
              >
                Cancel
              </Button>
              <PermissionGate permission="quotation_management" action="update">
                <Button
                  variant="primary"
                  className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                  onClick={handleConfirmScheduleReminder}
                >
                  Schedule Reminder
                </Button>
              </PermissionGate>
            </div>
          </div>
        </div>
      )}

      {/* Order Placed Confirmation Modal */}
      {showOrderPlacedConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Confirm Order Placed</h3>
              <button
                onClick={() => {
                  setShowOrderPlacedConfirm(false);
                  setMargOrderId('');
                }}
                className="text-gray-400 hover:text-gray-600"
                disabled={createOrderFromQuotationMutation.isPending}
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-6">
              <div className="flex items-start space-x-3 mb-4">
                <div className="w-full">
                  {/* MARG Order Id Input */}
                  <div className="mb-4 mt-3">
                    <label htmlFor="margOrderId" className="block text-sm font-medium text-gray-700 mb-1">
                      Enter Marg Order Number (APPL012345) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="margOrderId"
                      value={margOrderId}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val.length <= 20 && /^[a-zA-Z0-9\-_]*$/.test(val)) {
                          setMargOrderId(val);
                        }
                      }}
                      placeholder="e.g., APPL012345"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      required
                    />
                    <div className="flex justify-end mt-1">
                      <span className={`text-xs ${margOrderId.length >= 20 ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                        {margOrderId.length}/20
                      </span>
                    </div>
                  </div>

                  {quotationDetails && (
                    <div className="text-sm text-gray-600 space-y-1 bg-gray-50 p-3 rounded-md border border-gray-100">
                      <p>
                        <strong>Quote ID:</strong>{' '}
                        {quotationDetails.quoteNumber || quotationDetails._id || 'N/A'}
                      </p>
                      <p className="capitalize break-words">
                        <strong>Company:</strong>{' '}
                        {customerDisplay.companyName || (quotationDetails?.billingAddress?.company) || 'N/A'}
                      </p>
                      <p>
                        <strong>Quote Value:</strong> ₹{formatIndianAmount(quotationDetails.totalAmount || 0)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowOrderPlacedConfirm(false);
                  setMargOrderId('');
                }}
                disabled={createOrderFromQuotationMutation.isPending}
              >
                Cancel
              </Button>
              <PermissionGate permission="order_management" action="create">
                <Button
                  variant="primary"
                  onClick={() => createOrderFromQuotationMutation.mutate(margOrderId)}
                  disabled={createOrderFromQuotationMutation.isPending || !margOrderId.trim()}
                  className="bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  icon={<Package className="h-4 w-4" />}
                >
                  {createOrderFromQuotationMutation.isPending ? 'Creating...' : 'Confirm Order Placed'}
                </Button>
              </PermissionGate>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SendTrack;
